import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import { Add, Edit, Delete, Search, CheckCircle, Cancel } from "@mui/icons-material";
import { useTherapiesAdmin, useUsageCheck } from "../../../stores/MasterTables/therapies.queries";
import { useDeleteTherapy } from "../../../stores/MasterTables/therapies.mutations";
import { confirmDeleteWithUsage } from "./confirmDelete";
import ModalTherapy from "./ModalTherapy";
import type { UsePendingChangesReturn } from "./usePendingChanges";
import type { TherapyType } from "../../../api-client";

interface TherapiesTabProps {
  pending: UsePendingChangesReturn;
  therapyType?: TherapyType;
}

const TherapiesTab: React.FC<TherapiesTabProps> = ({ pending, therapyType }) => {
  const { data: therapies = [], isLoading } = useTherapiesAdmin(therapyType);
  const deleteTherapy = useDeleteTherapy();
  const checkUsage = useUsageCheck();

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return therapies;
    const s = search.toLowerCase();
    return therapies.filter(
      (t: any) =>
        t.id?.toLowerCase().includes(s) ||
        t.abbreviation?.toLowerCase().includes(s) ||
        t.description?.toLowerCase().includes(s)
    );
  }, [therapies, search]);

  const handleCreate = () => {
    setEditItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item: any) => {
    setEditItem(item);
    setModalOpen(true);
  };

  const handleDelete = async (item: any) => {
    const usage = await checkUsage("therapy", item.id);
    if (!confirmDeleteWithUsage(`Heilmittel ${item.abbreviation}`, usage)) return;
    deleteTherapy.mutate(item.id);
  };

  if (isLoading) {
    return (
      <Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <TextField
          placeholder="Suchen…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate} size="small">
          Neues Heilmittel
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Abkürzung</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Beschreibung</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Methode</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Position</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Tarifgruppe</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Dauer (Min)</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Zertifizierung</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Aktiv</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Ergänzung</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Preise</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {search ? "Keine Treffer" : "Keine Heilmittel vorhanden"}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t: any) => (
                <TableRow
                  key={t.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                  onClick={() => handleEdit(t)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#000" }}>
                      {t.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={t.abbreviation} size="small" sx={{ fontWeight: 600, color: "#000", borderColor: "#333" }} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ color: "#000" }}>{t.description}</TableCell>
                  {/* MD-232: the method is what ties this billable row back to a
                      prescribed term. Without one the row cannot be reached by
                      resolution at all, so say so rather than showing a blank. */}
                  <TableCell sx={{ color: "#000" }}>
                    {(t as any).therapyMethodId ? (
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {(t as any).therapyMethodId}
                      </Typography>
                    ) : (
                      <Tooltip title="Ohne Methode ist diese Zeile über die Auflösung nicht erreichbar">
                        <Chip label="keine" size="small" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ color: "#000" }}>{t.lineItem ?? "–"}</TableCell>
                  <TableCell sx={{ color: "#000" }}>{(typeof t.tarifGroup === "object" ? t.tarifGroup?.text || t.tarifGroup?.id : t.tarifGroup) || "–"}</TableCell>
                  <TableCell align="center" sx={{ color: "#000" }}>{t.duration ?? "–"}</TableCell>
                  <TableCell sx={{ color: "#000" }}>{t.therapyCertification?.displayName || "–"}</TableCell>
                  <TableCell align="center">
                    {t.inUse ? (
                      <CheckCircle fontSize="small" sx={{ color: "success.main" }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ color: "text.disabled" }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {t.isSupplementary ? (
                      <CheckCircle fontSize="small" sx={{ color: "info.main" }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ color: "text.disabled" }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={t.prices?.length ?? 0}
                      size="small"
                      variant="outlined"
                      color={t.prices?.length > 0 ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Bearbeiten">
                      <IconButton size="small" onClick={() => handleEdit(t)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton size="small" color="error" onClick={() => handleDelete(t)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        {filtered.length} von {therapies.length} Heilmittel
      </Typography>

      {/* Modal */}
      <ModalTherapy
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editItem={editItem}
      />
    </Box>
  );
};

export default TherapiesTab;
